import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from 'components/_new/Button';
import { Spinner } from 'components/_new/Spinner';
import { programsApi } from 'api';
import { useGlobal } from 'contexts/globalContext';
import { Dialog, DialogConfirm } from '../_new/Dialog';
import { Dropdown } from '../_new/Dropdown';

interface TransferApplicationDialogProps {
	visible: boolean;
	onHide: () => void;
	application: any;
	programId: number;
	onTransferSuccess: () => void;
}

interface ProgramMembership {
	id: number;
	user: {
		id: number;
		firstName: string;
		lastName: string;
		email: string;
	};
}

const TransferApplicationDialog: React.FC<TransferApplicationDialogProps> = ({
	visible,
	onHide,
	application,
	programId,
	onTransferSuccess,
}) => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();

	const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);
	const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);

	const {
		data: availableMemberships = [],
		isLoading: membershipLoading,
		isError: membershipError,
	} = useQuery(
		['getAvailableMembershipsForTransfer', programId, application?.applicationRole],
		() =>
			programsApi.getAvailableMembershipsForTransfer({
				programId,
				applicationRole: application?.applicationRole,
				excludeMembershipId: application?.programMembership?.id,
			}),
		{
			enabled: Boolean(visible && programId && application?.applicationRole),
		}
	);

	const { mutate: transferApplication, isLoading: transferLoading } = useMutation(
		programsApi.transferApplicationToMembership,
		{
			onSuccess: () => {
				toastRef.current?.show({
					severity: 'success',
					life: 5000,
					summary: t('misc.success'),
					detail: t('programPanel.application.transferApplicationSuccess'),
				});
				setConfirmDialogVisible(false);
				setSelectedMembershipId(null);
				onTransferSuccess();
				onHide();
			},
			onError: (error: any) => {
				toastRef.current?.show({
					severity: 'error',
					life: 5000,
					summary: t('misc.error'),
					detail: error?.message || t('programPanel.application.transferApplicationError'),
				});
			},
		}
	);

	const membershipOptions = useMemo(() => {
		return availableMemberships
			.sort((a: ProgramMembership, b: ProgramMembership) =>
				`${a.user.lastName} ${a.user.firstName}`.localeCompare(`${b.user.lastName} ${b.user.firstName}`, 'pl', {
					sensitivity: 'base',
				})
			)
			.map((membership: ProgramMembership) => ({
				value: membership.id,
				label: `${membership.user.lastName} ${membership.user.firstName}`,
				email: membership.user.email,
				userId: membership.user.id,
			}));
	}, [availableMemberships]);

	const selectedMembership = useMemo(() => {
		return membershipOptions.find((option) => option.value === selectedMembershipId);
	}, [membershipOptions, selectedMembershipId]);

	const handleTransfer = () => {
		if (!selectedMembershipId || !application?.id) return;

		transferApplication({
			applicationId: application.id,
			newMembershipId: selectedMembershipId,
			programId,
		});
	};

	const handleClose = () => {
		setSelectedMembershipId(null);
		setConfirmDialogVisible(false);
		onHide();
	};

	const itemTemplate = (option: any) => (
		<div className="flex flex-column">
			<span className="font-medium">{option.label}</span>
			<span className="text-sm text-gray-600">{option.email}</span>
		</div>
	);

	const valueTemplate = (option: any) => {
		if (!option) return null;
		return (
			<div className="flex flex-column">
				<span className="font-medium">{option.label}</span>
				<span className="text-sm text-gray-600">{option.email}</span>
			</div>
		);
	};

	return (
		<>
			<DialogConfirm
				visible={confirmDialogVisible}
				onHide={() => setConfirmDialogVisible(false)}
				onConfirm={() => handleTransfer()}
				message={
					selectedMembership
						? t('programPanel.application.confirmTransferMessage', {
								targetUser: selectedMembership.label,
								targetEmail: selectedMembership.email,
						  })
						: ''
				}
				// header={t('programPanel.application.confirmTransferTitle')}
				confirmAction={{
					label: t('programPanel.application.confirmTransfer'),
					loading: transferLoading,
				}}
				cancelAction
			/>
			<Dialog
				title={t('programPanel.application.transferApplicationTitle')}
				visible={visible}
				onHide={handleClose}
				className="w-11 md:w-6"
			>
				<div className="flex flex-column gap-4">
					<p className="m-0 text-gray-700">{t('programPanel.application.transferApplicationDescription')}</p>

					<div className="flex flex-column gap-2">
						<label htmlFor="membership-select" className="font-medium">
							{t('programPanel.application.selectTargetUser')}
						</label>

						{membershipLoading && (
							<div className="flex justify-content-center p-4">
								<Spinner size="sm" />
							</div>
						)}

						{membershipError && (
							<div className="text-red-500 text-sm">
								{t('programPanel.application.errorLoadingMemberships')}
							</div>
						)}

						{!membershipLoading && !membershipError && (
							<Dropdown
								id="membership-select"
								value={selectedMembershipId}
								onChange={(e) => setSelectedMembershipId(e.value)}
								options={membershipOptions}
								placeholder={t('programPanel.application.selectUserPlaceholder')}
								filter
								showClear={false}
								className="w-full"
								itemTemplate={itemTemplate}
								valueTemplate={valueTemplate}
								filterBy="label,email"
								emptyMessage={t('programPanel.application.noAvailableUsers')}
							/>
						)}
					</div>

					{selectedMembership && (
						<div className="bg-blue-50 border-round p-3">
							<div className="text-sm text-gray-700">
								<strong>{t('programPanel.application.selectedUser')}:</strong>
							</div>
							<div className="mt-1">
								<div className="font-medium">{selectedMembership.label}</div>
								<div className="text-sm text-gray-600">{selectedMembership.email}</div>
								<div className="text-xs text-gray-500">User ID: {selectedMembership.userId}</div>
							</div>
						</div>
					)}

					<div className="bg-yellow-50 border-round p-3">
						<div className="text-sm text-gray-700">
							<strong>{t('programPanel.application.transferWarning')}:</strong>
						</div>
						<ul className="text-sm text-gray-600 mt-2 mb-0 pl-3">
							<li>{t('programPanel.application.transferWarningApplication')}</li>
							<li>{t('programPanel.application.transferWarningPairs')}</li>
							<li>{t('programPanel.application.transferWarningContracts')}</li>
							<li>{t('programPanel.application.transferWarningGoals')}</li>
							<li>{t('programPanel.application.transferWarningSessions')}</li>
						</ul>
					</div>
					<div className="flex justify-content-end gap-2">
						<Button label={t('actions.cancel')} onClick={handleClose} disabled={transferLoading} />
						<Button
							label={t('programPanel.application.transferApplication')}
							onClick={() => setConfirmDialogVisible(true)}
							disabled={!selectedMembershipId || transferLoading}
							loading={transferLoading}
						/>
					</div>
				</div>
			</Dialog>
		</>
	);
};

export default TransferApplicationDialog;
